import { useState } from "react";

export default function ContactForm() {
  const [formData, setFormData] = useState({
    navn: "",
    telefon: "",
    email: "",
    beskrivelse: "",
    website_url: "" // Honeypot field
  });

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, success: false, error: null });

    // Enforce basic client-side validation
    if (!formData.navn || !formData.telefon || !formData.email || !formData.beskrivelse) {
      setStatus({
        submitting: false,
        success: false,
        error: "Venligst udfyld alle påkrævede felter."
      });
      return;
    }

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          name: formData.navn,
          phone: formData.telefon,
          email: formData.email,
          message: formData.beskrivelse,
          website_url: formData.website_url // Mandatory honeypot
        })
      });

      const result = await response.json();

      if (response.ok && result.status === "ok") {
        setStatus({
          submitting: false,
          success: true,
          error: null
        });
        setFormData({
          navn: "",
          telefon: "",
          email: "",
          beskrivelse: "",
          website_url: ""
        });
      } else {
        setStatus({
          submitting: false,
          success: false,
          error: result.message || "Der opstod en fejl under afsendelsen. Prøv venligst igen."
        });
      }
    } catch (err) {
      setStatus({
        submitting: false,
        success: false,
        error: "Netværksfejl. Kontroller din internetforbindelse og prøv igen."
      });
    }
  };

  return (
    <div className="rounded-2xl border border-brand-brown/10 bg-white p-6 shadow-xl sm:p-8">
      <h3 className="font-serif text-2xl font-bold text-brand-graphite mb-2">
        Send os en besked
      </h3>
      <p className="text-xs text-brand-graphite/70 mb-6">
        Udfyld formularen, så vender vi tilbage til dig hurtigst muligt med et uforpligtende tilbud.
      </p>

      {status.success ? (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-6 text-center animate-in fade-in duration-300">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 mb-4">
            <i className="fa-solid fa-check text-xl" />
          </div>
          <h4 className="font-serif text-lg font-bold text-emerald-900 mb-1">
            Besked modtaget!
          </h4>
          <p className="text-xs text-emerald-700">
            Mange tak for din henvendelse. Vi behandler din besked og vender tilbage til dig hurtigst muligt.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Honeypot field - absolutely hidden from humans */}
          <div className="sr-only absolute -top-[9999px] -left-[9999px]" aria-hidden="true">
            <label htmlFor="website_url">Do not fill this out if you are a human</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          {/* Navn */}
          <div>
            <label htmlFor="navn" className="block text-xs font-semibold uppercase tracking-wider text-brand-graphite/80 mb-1.5">
              Navn <span className="text-brand-brown">*</span>
            </label>
            <input
              type="text"
              id="navn"
              name="navn"
              required
              value={formData.navn}
              onChange={handleChange}
              placeholder="Dit fulde navn"
              className="w-full rounded-lg border border-brand-brown/20 bg-brand-beige/20 px-4 py-3 text-sm text-brand-graphite placeholder-brand-graphite/40 transition-all duration-200 focus:border-brand-brown focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-brown/10"
            />
          </div>

          {/* Telefonnummer & Email (Grid) */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="telefon" className="block text-xs font-semibold uppercase tracking-wider text-brand-graphite/80 mb-1.5">
                Telefonnummer <span className="text-brand-brown">*</span>
              </label>
              <input
                type="tel"
                id="telefon"
                name="telefon"
                required
                value={formData.telefon}
                onChange={handleChange}
                placeholder="F.eks. 53 80 25 24"
                className="w-full rounded-lg border border-brand-brown/20 bg-brand-beige/20 px-4 py-3 text-sm text-brand-graphite placeholder-brand-graphite/40 transition-all duration-200 focus:border-brand-brown focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-brown/10"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-brand-graphite/80 mb-1.5">
                E-mail <span className="text-brand-brown">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="din@email.dk"
                className="w-full rounded-lg border border-brand-brown/20 bg-brand-beige/20 px-4 py-3 text-sm text-brand-graphite placeholder-brand-graphite/40 transition-all duration-200 focus:border-brand-brown focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-brown/10"
              />
            </div>
          </div>

          {/* Beskrivelse */}
          <div>
            <label htmlFor="beskrivelse" className="block text-xs font-semibold uppercase tracking-wider text-brand-graphite/80 mb-1.5">
              Beskrivelse af opgaven <span className="text-brand-brown">*</span>
            </label>
            <textarea
              id="beskrivelse"
              name="beskrivelse"
              required
              rows="4"
              value={formData.beskrivelse}
              onChange={handleChange}
              placeholder="Beskriv dit projekt, dine ønsker og eventuelle mål..."
              className="w-full rounded-lg border border-brand-brown/20 bg-brand-beige/20 px-4 py-3 text-sm text-brand-graphite placeholder-brand-graphite/40 transition-all duration-200 focus:border-brand-brown focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-brown/10"
            />
          </div>

          {/* Error Message */}
          {status.error && (
            <div className="rounded-lg bg-rose-50 border border-rose-200 p-4 text-xs text-rose-700 flex items-center gap-2">
              <i className="fa-solid fa-circle-exclamation text-base text-rose-500" />
              <span>{status.error}</span>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={status.submitting}
            className="w-full flex items-center justify-center gap-2 rounded-full bg-brand-brown py-3.5 text-xs font-semibold uppercase tracking-wider text-white shadow-md transition-all duration-300 hover:bg-brand-brown-dark hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {status.submitting ? (
              <>
                <i className="fa-solid fa-spinner animate-spin" />
                Sender besked...
              </>
            ) : (
              <>
                <i className="fa-solid fa-paper-plane" />
                Send besked
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
